import { useEffect, useState } from "react";
import { useHistory, useLocation, matchPath } from "react-router-dom";
import { Col, Row, Space, Menu, Layout, Spin, message } from "antd";
import {
  LogoutOutlined, MenuFoldOutlined, MenuUnfoldOutlined,
} from "@ant-design/icons";

import { AUTHORIZATION_KEY } from "../../global_constants";
import { INDEX_ROUTE, SIDEBAR_ITEMS } from "../../routes";

import AccountHook from "../../hooks/account";
import RenderRoutes from "../../components/renderRoutes";
import { getApiKey } from "../../actions";

const { Header, Content, Sider } = Layout;

const DashboardLayout = ({ routes, accountData, setAccountData }) => {
  const history = useHistory();
  const { pathname } = useLocation();

  const [collapsed, setCollapsed] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState({});
  const [apiKey, setApiKey] = useState("");

  const specialMatchingRules = {
    "single-item": "single-items", // example: "single-item" is the name of the menu item, but the route is "single-items"
  }

  const matchPathForSelectedMenuOption = (pathCompare, submenuItem) => {
    for (const submenuOption of submenuItem) {
      if (submenuOption.submenu !== undefined) {
        matchPathForSelectedMenuOption(pathCompare, submenuOption.submenu);
        continue;
      }
      const matchedPath = matchPath(pathCompare, {
        path: submenuOption.route,
        exact: true,
      });
      if (matchedPath) {
        if (selectedMenu.name !== submenuOption.name) {
          setSelectedMenu(submenuOption);
          return;
        }
      }
    }
  }

  const loadApiKey = () => {
    getApiKey().then((response) => {
      const { data } = response;
      setApiKey(data.api_key);
    }).catch((err) => {
      message.error(err?.response?.data?.detail);
    });
  }

  const logout = () => {
    setAccountData({});
    history.push(INDEX_ROUTE);
    localStorage.removeItem(AUTHORIZATION_KEY);
  }

  const displaySubMenu = (submenuItem) => {
    if (submenuItem.submenu) {
      return (
        <Menu.SubMenu
          key={submenuItem.name}
          title={
            <>
              {submenuItem.icon}
              <span>{submenuItem.label}</span>
            </>
          }
        >
          {submenuItem.submenu.map((submenuItem) => {
            return displaySubMenu(submenuItem);
          })}
        </Menu.SubMenu>
      );
    }
    return (
      <Menu.Item
        key={submenuItem.name}
        icon={submenuItem.icon}
        onClick={() => history.push(submenuItem.route)}
      >
        {submenuItem.label}
      </Menu.Item>
    );

  }

  const userRoles = accountData?.user_roles || accountData?.roles || [];

  const isOkToLoadMainContent = () => {
    return accountData?.disabled === false;
  }

  useEffect(() => {
    if (accountData?.email !== undefined) {
      loadApiKey();
    }
  }, [accountData]);

  useEffect(() => {
    SIDEBAR_ITEMS.forEach((item) => {
      // matching child routes
      let pathCompare = pathname;
      let splittedPathname = pathname.split("/");
      if (splittedPathname.length > 2) {

        pathCompare = "/" + splittedPathname[1];

        if (specialMatchingRules[splittedPathname[1]] !== undefined) {
          pathCompare = "/" + specialMatchingRules[splittedPathname[1]];
        }
      }

      // if the route has submenus, we need to go through them
      if (item.submenu !== undefined) {
        matchPathForSelectedMenuOption(pathCompare, item.submenu);
      }

      const matchedPath = matchPath(pathCompare, {
        path: item.route,
        exact: true,
      });

      if (matchedPath) {
        if (selectedMenu.name !== item.name) {
          setSelectedMenu(item);
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const isOkToLoadMainContentFlag = isOkToLoadMainContent();

  return (
    <>
      <If condition={!isOkToLoadMainContentFlag}>
        <div style={{ "height": "100%", "display": "flex", "justifyContent": "center", "alignItems": "center" }}>
          <Spin />
        </div>
      </If>
      <If condition={isOkToLoadMainContentFlag}>
        <Layout className="main-layout">
          <Sider
            trigger={null}
            collapsible
            collapsed={collapsed}
          >
            {/* <div className="logo" /> */}
            <Menu
              theme="dark"
              mode="inline"
              selectedKeys={[selectedMenu.name]}
            >
              {SIDEBAR_ITEMS.map((item) => {
                // if the rolesAllowed property is set to side menu item, check
                // if the user has permissions to see/access it
                if (item.rolesAllowed !== undefined) {
                  const containsAtLeastOneAllowedRole = item.rolesAllowed.some(element => {
                    return userRoles.includes(element);
                  })
                  if (!containsAtLeastOneAllowedRole) {
                    return <></>
                  }
                }
                // if the rolesForbbiden property is set to side menu item, check
                // if the users role is mentioned. If it is, don't display the side menu item
                // this is used to have two different views for same menu option for different roles
                if (item.rolesForbbiden !== undefined) {
                  const containsAtLeastOneRoleNotAllowed = item.rolesForbbiden.some(element => {
                    return userRoles.includes(element);
                  })
                  if (containsAtLeastOneRoleNotAllowed) {
                    return <></>
                  }
                }
                let isExternalLink = item.isExternalLink;
                if (isExternalLink) {
                  return (
                    <Menu.Item
                      key={item.label}
                      icon={item.icon}
                    >
                      <a href={item.route} target="_blank" rel="noreferrer">{item.label}</a>
                    </Menu.Item>
                  )
                }
                if (item.submenu) {
                  return (
                    <Menu.SubMenu
                      key={item.name}
                      title={
                        <>
                          {item.icon}
                          <span>{item.label}</span>
                        </>
                      }
                    >
                      {item.submenu.map((submenuItem) => {
                        return displaySubMenu(submenuItem);
                      })}
                    </Menu.SubMenu>
                  );
                }
                return (
                  <Menu.Item
                    key={item.name}
                    icon={item.icon}
                    onClick={() => history.push(item.route)}
                  >
                    {item.label}
                  </Menu.Item>
                );
              })}
            </Menu>
          </Sider>
          <Layout>
            <Header>
              <Row justify="space-between" align="middle">
                <Col>
                  <Choose>
                    <When condition={collapsed}>
                      <MenuUnfoldOutlined
                        style={{ fontSize: "20px" }}
                        onClick={() => setCollapsed(!collapsed)}
                      />
                    </When>
                    <Otherwise>
                      <MenuFoldOutlined
                        style={{ fontSize: "20px" }}
                        onClick={() => setCollapsed(!collapsed)}
                      />
                    </Otherwise>
                  </Choose>
                </Col>
                <Col>
                  <Menu>
                    <Space>
                      <p> API Key: {apiKey} </p>
                      <Menu.Item key="logout" onClick={() => logout()}>
                        <Space>
                          <LogoutOutlined />
                          <p className="text-xl">Logout</p>
                        </Space>
                      </Menu.Item>
                    </Space>
                  </Menu>
                  {/* <Dropdown
                    arrow
                    trigger={["click"]}
                    overlay={
                      <Menu>
                        <Menu.Item key="logout" onClick={() => logout()}>
                          <Space key="13">
                            <LogoutOutlined key="14" />
                            <p key="15" className="text-xl">Logout</p>
                          </Space>
                        </Menu.Item>
                      </Menu>
                    }
                  >
                    <Space className="header-menu" key="16">
                      <Avatar key="17">{accountData.initials}</Avatar>
                      <p key="18" className="text-xl">{accountData.first_name + " " + accountData.last_name}</p>
                    </Space>
                  </Dropdown> */}
                </Col>
              </Row>
            </Header>
            <Content>
              <div className="main-container">
                <RenderRoutes routes={routes} />
              </div>
            </Content>
          </Layout>
        </Layout>
      </If>
    </>
  );
};

export default AccountHook(DashboardLayout);
